import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useAnnouncements } from '../contexts/AnnouncementContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AnnouncementEmailPanel } from '../components/AnnouncementEmailPanel';
import { Loader2, Bell, Mail, MailOpen, Trash2, Plus, Send, X, Calendar, Pencil } from 'lucide-react';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import api from '../services/api';

const WhatsNewPage = () => {
  const { user } = useAuth();
  const { refreshUnreadCount } = useAnnouncements();
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Founder create form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '' });

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState({ title: '', content: '' });

  // Announcement email state (shared by "Also send as email" on create, and "Email This Post")
  const [audienceCounts, setAudienceCounts] = useState({ all: 0, paid: 0, unpaid: 0, opted_out: 0 });
  const [countsLoading, setCountsLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testSentTo, setTestSentTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Create form: "Also send as email"
  const [sendEmail, setSendEmail] = useState(false);
  const [createAudience, setCreateAudience] = useState('all');
  const [showPublishEmailConfirm, setShowPublishEmailConfirm] = useState(false);
  const [publishedDraft, setPublishedDraft] = useState(null);

  // Email This Post (existing announcement)
  const [showEmailPostPanel, setShowEmailPostPanel] = useState(false);
  const [showEmailPostConfirm, setShowEmailPostConfirm] = useState(false);
  const [emailPostAudience, setEmailPostAudience] = useState('paid');

  const isFounder = user?.role === 'founder';

  const loadAudienceCounts = useCallback(async () => {
    setCountsLoading(true);
    try {
      const { data } = await api.get('/admin/announcements/audience-count');
      setAudienceCounts(data);
    } catch (err) {
      console.error('Failed to load audience counts:', err);
    } finally {
      setCountsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isFounder) loadAudienceCounts();
  }, [isFounder, loadAudienceCounts]);

  const handleSendTest = async (title, content) => {
    if (!title.trim() || !content.trim()) return;
    setSendingTest(true);
    setTestSentTo('');
    try {
      const { data } = await api.post('/admin/announcements/send-test', {
        subject: title.trim(),
        body: content.trim()
      });
      setTestSentTo(data.sent_to);
    } catch (err) {
      console.error('Failed to send test email:', err);
    } finally {
      setSendingTest(false);
    }
  };

  const handleConfirmPublishEmail = async () => {
    if (!publishedDraft) return;
    setEmailSending(true);
    setEmailError('');
    try {
      const { data } = await api.post(`/admin/announcements/${publishedDraft.id}/email`, {
        audience: createAudience
      });
      const emailed = {
        last_emailed_at: data.last_emailed_at,
        last_emailed_count: data.sent_count,
        last_emailed_audience: data.last_emailed_audience
      };
      setAnnouncements(prev => prev.map(a => a.id === publishedDraft.id ? { ...a, ...emailed } : a));
      setShowPublishEmailConfirm(false);
      setPublishedDraft(null);
      setSendEmail(false);
      setTestSentTo('');
      loadAudienceCounts();
      if (data.failed_count > 0) {
        toast.warning(`Emailed ${data.sent_count} users — ${data.failed_count} failed to deliver.`);
      } else {
        toast.success(`Emailed ${data.sent_count} user${data.sent_count !== 1 ? 's' : ''}.`);
      }
    } catch (err) {
      const message = err?.response?.data?.detail || 'Failed to send email';
      setEmailError(message);
      toast.error(message);
    } finally {
      setEmailSending(false);
    }
  };

  const handleOpenEmailThisPost = () => {
    setEmailPostAudience('paid');
    setTestSentTo('');
    setEmailError('');
    setShowEmailPostPanel(true);
  };

  const handleConfirmEmailPost = async () => {
    if (!selectedAnnouncement) return;
    setEmailSending(true);
    setEmailError('');
    try {
      const { data } = await api.post(`/admin/announcements/${selectedAnnouncement.id}/email`, {
        audience: emailPostAudience
      });
      const emailed = {
        last_emailed_at: data.last_emailed_at,
        last_emailed_count: data.sent_count,
        last_emailed_audience: data.last_emailed_audience
      };
      const updated = { ...selectedAnnouncement, ...emailed };
      setSelectedAnnouncement(updated);
      setAnnouncements(prev => prev.map(a => a.id === updated.id ? updated : a));
      setShowEmailPostConfirm(false);
      setShowEmailPostPanel(false);
      loadAudienceCounts();
      if (data.failed_count > 0) {
        toast.warning(`Emailed ${data.sent_count} users — ${data.failed_count} failed to deliver.`);
      } else {
        toast.success(`Emailed ${data.sent_count} user${data.sent_count !== 1 ? 's' : ''}.`);
      }
    } catch (err) {
      const message = err?.response?.data?.detail || 'Failed to send email';
      setEmailError(message);
      toast.error(message);
    } finally {
      setEmailSending(false);
    }
  };

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await api.get('/announcements');
      setAnnouncements(response.data.announcements);
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleOpenMessage = (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowModal(true);
    setIsEditMode(false);
    setEditData({ title: '', content: '' });
  };

  const handleStartEdit = () => {
    if (!selectedAnnouncement) return;
    setEditData({
      title: selectedAnnouncement.title,
      content: selectedAnnouncement.content
    });
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData({ title: '', content: '' });
  };

  const handleUpdateAnnouncement = async () => {
    if (!selectedAnnouncement || !editData.title.trim() || !editData.content.trim()) return;
    
    setEditLoading(true);
    try {
      const response = await api.put(`/announcements/${selectedAnnouncement.id}`, {
        title: editData.title.trim(),
        content: editData.content.trim()
      });
      
      // Update local state
      setAnnouncements(prev =>
        prev.map(a => a.id === selectedAnnouncement.id ? { ...a, ...response.data } : a)
      );
      setSelectedAnnouncement(response.data);
      setIsEditMode(false);
      setEditData({ title: '', content: '' });
    } catch (err) {
      console.error('Failed to update announcement:', err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (!selectedAnnouncement) return;
    
    try {
      await api.put(`/announcements/${selectedAnnouncement.id}/read`);
      // Update local state
      setAnnouncements(prev => 
        prev.map(a => a.id === selectedAnnouncement.id ? { ...a, is_read: true } : a)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      setSelectedAnnouncement(prev => ({ ...prev, is_read: true }));
      // Refresh global unread count for sidebar badge
      refreshUnreadCount();
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleDeleteForUser = async () => {
    if (!selectedAnnouncement) return;
    
    try {
      await api.delete(`/announcements/${selectedAnnouncement.id}/user`);
      // Remove from local state
      setAnnouncements(prev => prev.filter(a => a.id !== selectedAnnouncement.id));
      if (!selectedAnnouncement.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setShowModal(false);
      setSelectedAnnouncement(null);
      // Refresh global unread count for sidebar badge
      refreshUnreadCount();
    } catch (err) {
      console.error('Failed to delete announcement:', err);
    }
  };

  const handleDeleteGlobal = async () => {
    if (!selectedAnnouncement || user?.role !== 'founder') return;
    
    if (!window.confirm('Delete this announcement for ALL users? This cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/announcements/${selectedAnnouncement.id}`);
      // Remove from local state
      setAnnouncements(prev => prev.filter(a => a.id !== selectedAnnouncement.id));
      if (!selectedAnnouncement.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setShowModal(false);
      setSelectedAnnouncement(null);
      // Refresh global unread count for sidebar badge
      refreshUnreadCount();
    } catch (err) {
      console.error('Failed to delete announcement globally:', err);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) return;
    
    setCreateLoading(true);
    try {
      const response = await api.post('/announcements', {
        title: formData.title.trim(),
        content: formData.content.trim()
      });
      // Add to top of list
      setAnnouncements(prev => [response.data, ...prev]);
      setFormData({ title: '', content: '' });
      setShowCreateForm(false);
      if (sendEmail) {
        // Post is already published in-app. Confirm before also emailing it.
        setPublishedDraft(response.data);
        setShowPublishEmailConfirm(true);
      } else {
        setSendEmail(false);
      }
    } catch (err) {
      console.error('Failed to create announcement:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl" data-testid="whats-new-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-fsa-blue/10 rounded-sm flex items-center justify-center">
              <Bell className="w-5 h-5 text-fsa-blue" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-cockpit-text-primary">What's New</h1>
              <p className="text-sm text-cockpit-text-secondary">
                {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
          </div>
          
          {/* Founder: Create Button */}
          {isFounder && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="create-announcement-btn"
            >
              <Plus className="w-4 h-4" />
              New Announcement
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="card-panel p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-fsa-blue mx-auto" />
            <p className="text-cockpit-text-secondary mt-4">Loading announcements...</p>
          </div>
        ) : announcements.length === 0 ? (
          /* Empty State */
          <div className="card-panel p-12 text-center">
            <Bell className="w-12 h-12 text-cockpit-text-secondary/30 mx-auto mb-4" />
            <p className="text-cockpit-text-secondary">No announcements yet</p>
            {isFounder && (
              <p className="text-sm text-cockpit-text-secondary/60 mt-2">
                Click "New Announcement" to broadcast a message to all users.
              </p>
            )}
          </div>
        ) : (
          /* Message List */
          <div className="space-y-2">
            {announcements.map((announcement) => (
              <button
                key={announcement.id}
                onClick={() => handleOpenMessage(announcement)}
                className={`w-full text-left card-panel p-4 transition-all duration-150 hover:bg-cockpit-border/30 ${
                  !announcement.is_read ? 'border-l-2 border-l-fsa-blue' : ''
                }`}
                data-testid={`announcement-${announcement.id}`}
              >
                <div className="flex items-start gap-3">
                  {/* Read/Unread Icon */}
                  <div className={`mt-0.5 ${!announcement.is_read ? 'text-fsa-blue' : 'text-cockpit-text-secondary/40'}`}>
                    {announcement.is_read ? (
                      <MailOpen className="w-5 h-5" />
                    ) : (
                      <Mail className="w-5 h-5" />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium truncate ${
                        !announcement.is_read ? 'text-cockpit-text-primary' : 'text-cockpit-text-secondary'
                      }`}>
                        {announcement.title}
                      </h3>
                      {!announcement.is_read && (
                        <span className="flex-shrink-0 w-2 h-2 bg-fsa-blue rounded-full" />
                      )}
                    </div>
                    <p className="text-sm text-cockpit-text-secondary/60 mt-1 line-clamp-1">
                      {announcement.content}
                    </p>
                  </div>
                  
                  {/* Date */}
                  <div className="flex-shrink-0 text-xs text-cockpit-text-secondary/50 text-right">
                    <div>{formatDate(announcement.created_at)}</div>
                    {announcement.last_emailed_count != null && (
                      <div className="text-status-success mt-0.5" data-testid={`emailed-badge-${announcement.id}`}>
                        Emailed to {announcement.last_emailed_count}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Message Modal */}
        {showModal && selectedAnnouncement && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            data-testid="announcement-modal"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => {
                setShowModal(false);
                setSelectedAnnouncement(null);
                setIsEditMode(false);
                setEditData({ title: '', content: '' });
              }}
            />
            
            {/* Modal */}
            <div 
              className="relative w-full max-w-2xl max-h-[90vh] bg-cockpit-panel border border-cockpit-border rounded-lg shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-cockpit-border flex-shrink-0">
                <div className="flex-1 min-w-0 pr-4">
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editData.title}
                      onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                      className="input-field w-full text-xl font-semibold"
                      placeholder="Announcement title"
                      maxLength={100}
                      data-testid="edit-title-input"
                    />
                  ) : (
                    <h2 className="text-xl font-semibold text-cockpit-text-primary">
                      {selectedAnnouncement.title}
                    </h2>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-sm text-cockpit-text-secondary/60">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(selectedAnnouncement.created_at)}
                    </span>
                    {selectedAnnouncement.updated_at && (
                      <span className="text-cockpit-text-secondary/50 italic">Edited</span>
                    )}
                    {!selectedAnnouncement.is_read && !isEditMode && (
                      <span className="text-fsa-blue font-medium">Unread</span>
                    )}
                    {selectedAnnouncement.last_emailed_count != null && !isEditMode && (
                      <span className="text-status-success font-medium" data-testid="emailed-badge-modal">
                        Emailed to {selectedAnnouncement.last_emailed_count} users on {formatDate(selectedAnnouncement.last_emailed_at)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedAnnouncement(null);
                    setIsEditMode(false);
                    setEditData({ title: '', content: '' });
                  }}
                  className="text-cockpit-text-secondary hover:text-cockpit-text-primary transition-colors"
                  data-testid="close-announcement-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {isEditMode ? (
                  <div>
                    <textarea
                      value={editData.content}
                      onChange={(e) => setEditData(prev => ({ ...prev, content: e.target.value }))}
                      className="input-field w-full min-h-[200px] resize-y"
                      placeholder="Announcement content..."
                      maxLength={5000}
                      data-testid="edit-content-input"
                    />
                    <p className="text-xs text-cockpit-text-secondary/50 mt-1">
                      {editData.content.length}/5000 characters
                    </p>
                  </div>
                ) : (
                  <div className="prose prose-invert prose-sm max-w-none">
                    <p className="text-cockpit-text-secondary whitespace-pre-wrap leading-relaxed">
                      {selectedAnnouncement.content}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex items-center justify-between p-6 border-t border-cockpit-border bg-cockpit-bg/50 flex-shrink-0">
                {isEditMode ? (
                  /* Edit Mode Actions */
                  <>
                    <button
                      onClick={handleCancelEdit}
                      className="btn-secondary"
                      data-testid="cancel-edit"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateAnnouncement}
                      disabled={editLoading || !editData.title.trim() || !editData.content.trim()}
                      className="btn-primary flex items-center gap-2"
                      data-testid="update-announcement"
                    >
                      {editLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Update Message
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  /* View Mode Actions */
                  <>
                    <div className="flex gap-2">
                      {/* Delete for user */}
                      <button
                        onClick={handleDeleteForUser}
                        className="btn-secondary flex items-center gap-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        data-testid="delete-announcement-user"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                      
                      {/* Founder: Delete Global */}
                      {isFounder && (
                        <button
                          onClick={handleDeleteGlobal}
                          className="btn-secondary flex items-center gap-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          data-testid="delete-announcement-global"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete for All
                        </button>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      {/* Founder: Email This Post */}
                      {isFounder && (
                        <button
                          onClick={handleOpenEmailThisPost}
                          className="btn-secondary flex items-center gap-2"
                          data-testid="email-this-post-btn"
                        >
                          <Send className="w-4 h-4" />
                          Email This Post
                        </button>
                      )}

                      {/* Founder: Edit */}
                      {isFounder && (
                        <button
                          onClick={handleStartEdit}
                          className="btn-secondary flex items-center gap-2"
                          data-testid="edit-announcement"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit Message
                        </button>
                      )}
                      
                      {/* Mark as Read */}
                      {!selectedAnnouncement.is_read && (
                        <button
                          onClick={handleMarkAsRead}
                          className="btn-primary flex items-center gap-2"
                          data-testid="mark-as-read"
                        >
                          <MailOpen className="w-4 h-4" />
                          Mark as Read
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Create Announcement Modal (Founder Only) */}
        {showCreateForm && isFounder && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            data-testid="create-announcement-modal"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setShowCreateForm(false)}
            />
            
            {/* Modal */}
            <div 
              className="relative w-full max-w-2xl max-h-[90vh] bg-cockpit-panel border border-cockpit-border rounded-lg shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-cockpit-border flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-fsa-blue/10 rounded-lg flex items-center justify-center">
                    <Send className="w-5 h-5 text-fsa-blue" />
                  </div>
                  <h2 className="text-lg font-semibold text-cockpit-text-primary">
                    Broadcast Announcement
                  </h2>
                </div>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-cockpit-text-secondary hover:text-cockpit-text-primary transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Form */}
              <form onSubmit={handleCreateAnnouncement} className="flex flex-col flex-1 min-h-0">
                <div className="p-6 space-y-5 overflow-y-auto flex-1">
                  <div>
                    <Label className="label-text block mb-2">Title</Label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Announcement title"
                      maxLength={100}
                      data-testid="announcement-title-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="label-text block mb-2">Message Content</Label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                      className="input-field w-full min-h-[200px] resize-y"
                      placeholder="Write your announcement here. This will be sent to all users..."
                      maxLength={5000}
                      data-testid="announcement-content-input"
                    />
                    <p className="text-xs text-cockpit-text-secondary/50 mt-1">
                      {formData.content.length}/5000 characters
                    </p>
                  </div>

                  {/* Also send as email */}
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        className="accent-fsa-blue w-4 h-4"
                        data-testid="also-send-email-checkbox"
                      />
                      <span className="label-text">Also send as email</span>
                    </label>

                    {sendEmail && (
                      <AnnouncementEmailPanel
                        title={formData.title}
                        content={formData.content}
                        audience={createAudience}
                        onAudienceChange={setCreateAudience}
                        counts={audienceCounts}
                        countsLoading={countsLoading}
                        onSendTest={() => handleSendTest(formData.title, formData.content)}
                        sendingTest={sendingTest}
                        testSentTo={testSentTo}
                      />
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 p-6 border-t border-cockpit-border flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading || !formData.title.trim() || !formData.content.trim()}
                    className="btn-primary flex items-center gap-2"
                    data-testid="publish-announcement"
                  >
                    {createLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Publish Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Publish + Email Confirmation */}
        <AlertDialog
          open={showPublishEmailConfirm}
          onOpenChange={(open) => {
            if (!open) {
              setShowPublishEmailConfirm(false);
              setPublishedDraft(null);
              setSendEmail(false);
            }
          }}
        >
          <AlertDialogContent className="bg-cockpit-panel border-cockpit-border max-w-md" data-testid="publish-email-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-fsa-blue flex items-center gap-2">
                <Send className="w-5 h-5" />
                Publish and email {audienceCounts[createAudience] ?? 0} user{(audienceCounts[createAudience] ?? 0) !== 1 ? 's' : ''}?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="text-cockpit-text-secondary space-y-2">
                  <p className="text-xs">The announcement has already been posted in-app. Send it as an email too?</p>
                  {emailError && (
                    <p className="text-xs text-red-400" data-testid="publish-email-error">{emailError}</p>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="btn-secondary"
                onClick={() => {
                  setShowPublishEmailConfirm(false);
                  setPublishedDraft(null);
                  setSendEmail(false);
                }}
                data-testid="skip-publish-email-btn"
              >
                Skip Email
              </AlertDialogCancel>
              <button
                onClick={handleConfirmPublishEmail}
                disabled={emailSending}
                className="px-4 py-2 text-sm font-medium rounded-sm bg-fsa-blue text-white hover:bg-fsa-blue/80 transition-colors disabled:opacity-60"
                data-testid="confirm-publish-email-btn"
              >
                {emailSending ? 'Sending...' : `Email ${audienceCounts[createAudience] ?? 0} User${(audienceCounts[createAudience] ?? 0) !== 1 ? 's' : ''}`}
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Email This Post Modal (existing announcement) */}
        {showEmailPostPanel && selectedAnnouncement && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            data-testid="email-post-modal"
          >
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setShowEmailPostPanel(false)}
            />
            <div
              className="relative w-full max-w-xl max-h-[90vh] bg-cockpit-panel border border-cockpit-border rounded-lg shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-cockpit-border flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-fsa-blue/10 rounded-lg flex items-center justify-center">
                    <Send className="w-5 h-5 text-fsa-blue" />
                  </div>
                  <h2 className="text-lg font-semibold text-cockpit-text-primary">Email This Post</h2>
                </div>
                <button
                  onClick={() => setShowEmailPostPanel(false)}
                  className="text-cockpit-text-secondary hover:text-cockpit-text-primary transition-colors"
                  data-testid="close-email-post-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <AnnouncementEmailPanel
                  title={selectedAnnouncement.title}
                  content={selectedAnnouncement.content}
                  audience={emailPostAudience}
                  onAudienceChange={setEmailPostAudience}
                  counts={audienceCounts}
                  countsLoading={countsLoading}
                  onSendTest={() => handleSendTest(selectedAnnouncement.title, selectedAnnouncement.content)}
                  sendingTest={sendingTest}
                  testSentTo={testSentTo}
                />
                {emailError && (
                  <p className="text-xs text-red-400 mt-3" data-testid="email-post-error">{emailError}</p>
                )}
              </div>
              <div className="flex justify-end gap-3 p-6 border-t border-cockpit-border flex-shrink-0">
                <button onClick={() => setShowEmailPostPanel(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => setShowEmailPostConfirm(true)}
                  disabled={!audienceCounts[emailPostAudience]}
                  className="btn-primary flex items-center gap-2"
                  data-testid="email-post-send-btn"
                >
                  <Send className="w-4 h-4" />
                  Email to {audienceCounts[emailPostAudience] ?? 0} Users
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email This Post Confirmation */}
        <AlertDialog open={showEmailPostConfirm} onOpenChange={setShowEmailPostConfirm}>
          <AlertDialogContent className="bg-cockpit-panel border-cockpit-border max-w-md" data-testid="email-post-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-fsa-blue flex items-center gap-2">
                <Send className="w-5 h-5" />
                Email this post to {audienceCounts[emailPostAudience] ?? 0} user{(audienceCounts[emailPostAudience] ?? 0) !== 1 ? 's' : ''}?
              </AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
              <button
                onClick={handleConfirmEmailPost}
                disabled={emailSending}
                className="px-4 py-2 text-sm font-medium rounded-sm bg-fsa-blue text-white hover:bg-fsa-blue/80 transition-colors disabled:opacity-60"
                data-testid="confirm-email-post-btn"
              >
                {emailSending ? 'Sending...' : 'Confirm & Send'}
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default WhatsNewPage;
